import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {
	reviewInformationScreenView,
	reviewInformationScreenContactLink,
	reviewInformationScreenSettingsLink,
} from 'stash/actions/debitSignUpTrackingEvents';

import {
	Text,
	Box,
	Flex,
	Link,
	Divider,
	PrimaryButton,
	SmartLink,
} from '@stashinvest/ui';
import { CONTACT_URL } from 'stash/constants';

import {
	SignUpWrapper,
	SignUpWrapperInner,
} from 'stash/components/sign-up/SignUpScreenLayout';

const InfoLabel = styled(Text.Title).attrs({
	forwardedAs: 'dt',
})``;

const InfoText = styled(Text).attrs({
	forwardedAs: 'dd',
	mb: 'm',
})``;

const DisclosureText = styled(Text.BodySmall).attrs({ mb: 'm' })``;

const mapDispatchToProps = {
	reviewInformationScreenView,
	reviewInformationScreenSettingsLink,
	reviewInformationScreenContactLink,
};

function ConfirmInfoScreen(props) {
	const {
		reviewInformationScreenView,
		reviewInformationScreenSettingsLink,
		reviewInformationScreenContactLink,
	} = props;

	useEffect(() => {
		reviewInformationScreenView();
	}, [reviewInformationScreenView]);

	return (
		<SignUpWrapper>
			<Text.H1 size={3} mb="m">
				Perfect! Please confirm the information below is correct.
			</Text.H1>
			<SignUpWrapperInner>
				<Box as="dl" mb="l">
					<InfoLabel>Legal first name</InfoLabel>
					<InfoText>{props.firstName}</InfoText>

					<InfoLabel>Legal last name</InfoLabel>
					<InfoText>{props.lastName}</InfoText>

					<Flex alignItems="baseline">
						<InfoLabel mr="xxs">Residential address</InfoLabel>
						<Link as="button" type="button" onClick={props.handleEditClick}>
							Edit
						</Link>
					</Flex>
					<InfoText>
						{props.addressLine1}
						{props.addressLine2 && `, ${props.addressLine2}`}
						<br />
						{props.city}, {props.state} {props.postalCode}
					</InfoText>

					<InfoLabel>Date of birth</InfoLabel>
					<InfoText>{props.dateOfBirth}</InfoText>

					<InfoLabel>Social Security number</InfoLabel>
					<InfoText>{props.maskedSsn}</InfoText>

					<InfoLabel>Phone number</InfoLabel>
					<InfoText>{props.phoneNumber}</InfoText>

					<InfoLabel>Primary email address</InfoLabel>
					<InfoText>{props.email}</InfoText>
				</Box>

				<Text mb="s">
					To change your email or phone, go to{' '}
					<Link
						as={SmartLink}
						to={'/account-settings'}
						onClick={reviewInformationScreenSettingsLink}
					>
						your settings
					</Link>
					.
				</Text>

				<Text mb="m">
					For other changes,{' '}
					<Link
						as={SmartLink}
						to={CONTACT_URL}
						onClick={reviewInformationScreenContactLink}
					>
						contact us
					</Link>
					.
				</Text>

				<PrimaryButton
					type="submit"
					loading={props.isSubmitting}
					onClick={props.handleCtaClick}
				>
					Save and continue
				</PrimaryButton>
				<Divider my="m" />
				<DisclosureText>
					<strong>
						Important information about procedures for opening a new account:{' '}
					</strong>
					To help the government fight the funding of terrorism and money laundering
					activities, federal law requires all financial institutions to obtain, verify,
					and record information that identifies each person who opens an Account.
				</DisclosureText>
				<DisclosureText>
					What this means for you: When you open an Account, we will ask for your name,
					address, date of birth, and other information that will allow us to identify
					you. We may also ask to see a copy of your driver’s license or other identifying
					documents. Account opening is also subject to other fraud prevention measures.
					While there is no credit check to get a card, certain features may be subject to
					the use of a consumer report. You will be notified prior to any use.
				</DisclosureText>
			</SignUpWrapperInner>
		</SignUpWrapper>
	);
}

ConfirmInfoScreen.propTypes = {
	addressLine1: PropTypes.string.isRequired,
	addressLine2: PropTypes.string,
	city: PropTypes.string.isRequired,
	state: PropTypes.string.isRequired,
	postalCode: PropTypes.string.isRequired,
	firstName: PropTypes.string.isRequired,
	lastName: PropTypes.string.isRequired,
	dateOfBirth: PropTypes.string.isRequired,
	socialSecurityNumber: PropTypes.string.isRequired,
	phoneNumber: PropTypes.string.isRequired,
	email: PropTypes.string.isRequired,
	isPosting: PropTypes.bool,
	handleCtaClick: PropTypes.func.isRequired,
	handleEditClick: PropTypes.func.isRequired,
};

export default connect(null, mapDispatchToProps)(ConfirmInfoScreen);
